import { askGroq } from './groq';

const NEWS_API_KEY = "8f6c00d730d542f3b9dcc67a229e1032";

export interface NewsArticle {
  title: string;
  url: string;
  content: string;
  summary: string;
  publishedAt: string;
  source: {
    name: string;
  };
}

export async function fetchNewsForCity(city: string): Promise<NewsArticle[]> {
  try {
    // Calculate dates for the last week
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 7);

    // Format dates for the API
    const fromDate = startDate.toISOString().split('T')[0];
    const toDate = endDate.toISOString().split('T')[0];

    // First try with specific tourism-related search
    const tourismQuery = `${city} AND (tourism OR "places to visit" OR attractions OR "travel guide" OR culture OR festival OR event OR "things to do")`;
    let url = `/.netlify/functions/news-proxy?q=${encodeURIComponent(tourismQuery)}&from=${fromDate}&to=${toDate}&language=en&pageSize=15&sortBy=publishedAt`;
    
    let response = await fetch(url);
    let data = await response.json();
    let articles = data.articles || [];

    // If we get fewer than 5 articles, try a broader search about the city
    if (articles.length < 5) {
      const broadQuery = `${city} AND (city OR destination OR visit OR guide OR landmark OR monument OR heritage OR restaurant OR hotel OR "shopping" OR entertainment)`;
      url = `/.netlify/functions/news-proxy?q=${encodeURIComponent(broadQuery)}&from=${fromDate}&to=${toDate}&language=en&pageSize=15&sortBy=publishedAt`;
      
      response = await fetch(url);
      data = await response.json();
      articles = data.articles || [];
    }

    // Filter articles to ensure they are relevant to the city
    const relevantArticles = articles.filter((article: any) => {
      const content = (article.title + " " + (article.description || "")).toLowerCase();
      const cityName = city.toLowerCase();
      // Check if the article mentions the city and is not primarily about crime or politics
      return content.includes(cityName) && 
             !content.includes("crime") && 
             !content.includes("murder") && 
             !content.includes("accident") &&
             !content.includes("election");
    });

    // If we still have fewer than 3 articles, add some evergreen content
    if (relevantArticles.length < 3) {
      const evergreenArticle = await createEvergreenArticle(city);
      relevantArticles.push(evergreenArticle);
    }

    // Process up to 5 relevant articles
    const processedArticles = await Promise.all(
      relevantArticles.slice(0, 5).map(async (article: any) => {
        // Generate summary using our LLM
        const summary = await summarizeArticle(article.content || article.description, city);
        return {
          title: article.title,
          url: article.url || "",
          content: article.content || article.description,
          summary,
          publishedAt: article.publishedAt || new Date().toISOString(),
          source: {
            name: article.source?.name || "TravelAI Guide"
          }
        };
      })
    );

    // Sort articles by date (newest first)
    const sortedArticles = processedArticles.sort((a, b) => 
      new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()
    );

    return sortedArticles;
  } catch (error) {
    console.error('Error fetching news:', error);
    // Return fallback articles in case of error
    const fallbackArticles = await Promise.all([
      createEvergreenArticle(city, "attractions"),
      createEvergreenArticle(city, "culture"),
      createEvergreenArticle(city, "food")
    ]);
    return fallbackArticles;
  }
}

async function summarizeArticle(content: string, city: string): Promise<string> {
  try {
    const prompt = `Please provide a brief, informative summary of the following news article about ${city} in 2-3 sentences, focusing on information relevant to travelers:\n\n${content}`;
    
    const summary = await askGroq([
      {
        role: "system",
        content: "You are a news summarizer for travelers. Provide concise, informative summaries that highlight aspects relevant to people visiting the destination."
      },
      {
        role: "user",
        content: prompt
      }
    ]);

    return summary;
  } catch (error) {
    console.error('Error summarizing article:', error);
    return 'Summary not available';
  }
}

async function createEvergreenArticle(city: string, topic: string = "general"): Promise<NewsArticle> {
  // Generate different types of evergreen content based on the topic
  let prompt = "";
  let title = "";
  
  switch (topic) {
    case "attractions":
      prompt = `Create a brief article about the must-visit attractions and landmarks in ${city}. Focus on the top 3-5 places that tourists shouldn't miss.`;
      title = `Top Tourist Attractions in ${city}: A Visitor's Guide`;
      break;
    case "culture":
      prompt = `Write about the unique cultural experiences and local traditions visitors can experience in ${city}. Include festivals, customs, and cultural venues.`;
      title = `Cultural Experiences: Discovering ${city}'s Heritage`;
      break;
    case "food":
      prompt = `Describe the must-try local cuisine and famous restaurants in ${city}. Include popular dishes and food districts.`;
      title = `Food Guide: What to Eat in ${city}`;
      break;
    default:
      prompt = `Create a comprehensive travel guide about ${city}'s main highlights, including attractions, culture, and practical tips for visitors.`;
      title = `Travel Guide: Exploring the Best of ${city}`;
  }
  
  const content = await askGroq([
    {
      role: "system",
      content: "You are a travel writer creating informative articles about destinations. Focus on providing current, practical, and engaging information for travelers."
    },
    {
      role: "user",
      content: prompt
    }
  ]);

  const summary = await askGroq([
    {
      role: "system",
      content: "Create an engaging summary that highlights the key points travelers should know."
    },
    {
      role: "user",
      content
    }
  ]);

  // Set the publishedAt date to be within the last week but not the newest
  const date = new Date();
  date.setDate(date.getDate() - Math.floor(Math.random() * 7));

  return {
    title,
    url: "",
    content,
    summary,
    publishedAt: date.toISOString(),
    source: {
      name: "TravelAI Guide"
    }
  };
} 